import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import clsx from "clsx";

import { pillBaseClasses } from "./ticketMeta";

interface TicketPillMenuProps<T extends number> {
  value: T;
  options: T[];
  labels: Record<T, string>;
  classMap: Record<T, string>;
  onSelect: (next: T) => void;
  isPending?: boolean;
  title?: string;
  /** Shown above the options, for pills whose value alone doesn't say what it is. */
  heading?: string;
  /** Which edge of the pill the dropdown lines up with - use "right" near a container's right edge. */
  align?: "left" | "right";
  className?: string;
}

/** A pill that doubles as its own dropdown editor. */
export default function TicketPillMenu<T extends number>({
  value,
  options,
  labels,
  classMap,
  onSelect,
  isPending,
  title,
  heading,
  align = "left",
  className,
}: TicketPillMenuProps<T>) {
  return (
    // Stops the row's navigation handler firing when the menu is used.
    <div
      className={clsx("relative inline-block text-left", className)}
      onClick={(e) => e.stopPropagation()}
    >
      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button
          className={clsx(
            pillBaseClasses,
            classMap[value],
            "hover:ring-2 hover:ring-orange-300",
            isPending ? "opacity-60" : "",
          )}
          title={title}
        >
          {labels[value]}
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className={clsx(
              "absolute z-20 mt-1 w-40 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
              align === "right"
                ? "right-0 origin-top-right"
                : "left-0 origin-top-left",
            )}
          >
            {heading ? (
              <p className="px-3 pb-1 pt-1.5 text-xs font-medium text-gray-500">
                {heading}
              </p>
            ) : null}
            {options.map((option) => (
              <Menu.Item key={option}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => {
                      if (option !== value) onSelect(option);
                    }}
                    className={clsx(
                      "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                      active ? "bg-gray-100" : "",
                      option === value ? "font-semibold" : "",
                    )}
                  >
                    <span
                      className={clsx(
                        "h-2.5 w-2.5 rounded-full",
                        classMap[option].split(" ")[0],
                      )}
                    />
                    {labels[option]}
                  </button>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
}
